"use client";

import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { KTX2Loader } from "three/examples/jsm/loaders/KTX2Loader.js";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";

export default function DesertPage() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    // Load lil-gui dynamically
    let guiInstance: any = null;

    const canvas = canvasRef.current;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    let W = window.innerWidth, H = window.innerHeight;
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;

    const clock = new THREE.Clock();
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, W / H, 0.1, 800);
    camera.position.set(0, 2.5, 13);
    camera.lookAt(0, 0, 0);

    const P = {
      windStrength: 1.0,
      windAngle: 12,
      windRipple: 1.4,
      sandLight: "#bf6c22",
      sandDark: "#1a0700",
      sandCrest: "#ffc05a",
      dustOn: true,
      dustCount: 2000,
      dustOpacity: 0.65,
      dustSize: 1.8,
      lightColor: "#c26500",
      lightIntensity: 2.8,
      bloomStr: 2.0,
      vignette: 0.55,
      grain: 0.028,
      camY: 2.5,
      camZ: 13.0,
    };

    function windVec2() {
      const r = (P.windAngle * Math.PI) / 180;
      return new THREE.Vector2(Math.cos(r), Math.sin(r));
    }

    scene.add(new THREE.AmbientLight(0xffeedd, 0.45));

    const sunLight = new THREE.DirectionalLight(new THREE.Color(P.lightColor), P.lightIntensity);
    sunLight.position.set(-8, 4, -12);
    scene.add(sunLight);

    const bgScene = new THREE.Scene();
    const bgCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    const skyMat = new THREE.ShaderMaterial({
      depthWrite: false,
      depthTest: false,
      uniforms: { uRes: { value: new THREE.Vector2(W, H) } },
      vertexShader: `void main(){ gl_Position = vec4(position.xy,1.,1.); }`,
      fragmentShader: `
        uniform vec2 uRes;
        void main(){
          vec2 uv = gl_FragCoord.xy / uRes;
          vec3 top = vec3(0.04, 0.015, 0.005);
          vec3 mid = vec3(0.52, 0.18,  0.03);
          vec3 bot = vec3(0.88, 0.42,  0.06);
          vec3 c   = mix(bot, mid, smoothstep(0.0, 0.38, uv.y));
              c   = mix(c,   mid, smoothstep(0.38,0.55, uv.y));
              c   = mix(c,   top, smoothstep(0.55,1.0,  uv.y));
          vec2 s = vec2(0.30, 0.44);
          float d = length(uv - s);
          c += vec3(1.,.98,.8) * smoothstep(0.022,0.014, d) * 2.2;
          c += vec3(1.,.72,.2) * exp(-d * 11.0) * 1.6;
          c += vec3(1.,.55,.1) * exp(-d *  3.5) * 0.45;
          gl_FragColor = vec4(c, 1.);
        }
      `,
    });
    bgScene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), skyMat));

    const SEG = 320;
    const SIZE = 130;
    const sandGeo = new THREE.PlaneGeometry(SIZE, SIZE, SEG, SEG);
    sandGeo.rotateX(-Math.PI / 2);

    function _h(x: number, z: number) {
      const v = Math.sin(x * 127.1 + z * 311.7) * 43758.5453;
      return v - Math.floor(v);
    }
    function _n(x: number, z: number) {
      const ix = Math.floor(x), iz = Math.floor(z);
      const fx = x - ix, fz = z - iz;
      const ux = fx * fx * (3 - 2 * fx), uz = fz * fz * (3 - 2 * fz);
      return _h(ix, iz) * (1 - ux) * (1 - uz) + _h(ix + 1, iz) * ux * (1 - uz) + _h(ix, iz + 1) * (1 - ux) * uz + _h(ix + 1, iz + 1) * ux * uz;
    }
    function fbm(x: number, z: number, oct = 6) {
      let v = 0, a = 0.5, sx = x, sz = z;
      for (let i = 0; i < oct; i++) {
        v += a * _n(sx, sz);
        const nx = sx * 1.6 + sz * 1.2;
        const nz = -sx * 1.2 + sz * 1.6;
        sx = nx;
        sz = nz;
        a *= 0.5;
      }
      return v;
    }

    const posArr = sandGeo.attributes.position;
    for (let i = 0; i < posArr.count; i++) {
      const x = posArr.getX(i);
      const z = posArr.getZ(i);
      const nu = (x / SIZE) * 4.0;
      const nv = (z / SIZE) * 3.5;
      const base = fbm(nu, nv, 6);
      const windRad = (P.windAngle * Math.PI) / 180;
      const along = x * Math.cos(windRad) + z * Math.sin(windRad);
      const crest = Math.pow(Math.abs(Math.sin(along * 0.55 + base * 3.5)), 2.5);
      const perp = -x * Math.sin(windRad) + z * Math.cos(windRad);
      const ripple = Math.pow(Math.abs(Math.sin(perp * 1.8 + base * 6.0)), 3.5) * 0.18;
      const h = crest * 1.9 + ripple - 4.8;
      posArr.setY(i, h);
    }
    sandGeo.computeVertexNormals();
    posArr.needsUpdate = true;

    const textureLoader = new THREE.TextureLoader();
    const photoTex = textureLoader.load(
      "/models/assets/ChatGPT Image 5 Jun 2026, 02.47.16.png",
      (tex) => {
        tex.mapping = THREE.CubeReflectionMapping;
        tex.wrapS = THREE.ClampToEdgeWrapping;
        tex.wrapT = THREE.ClampToEdgeWrapping;
        tex.repeat.set(2.84, 1.39);
        tex.center.set(0.35, 1.27);
        tex.offset.set(0.3, 0.3);
        tex.flipY = true;
        tex.colorSpace = THREE.SRGBColorSpace;
        sphereMat.envMap = tex;
        sphereMat.needsUpdate = true;
      }
    );

    const light = new THREE.PointLight(0xffddaa, 0.8, 10, 2);
    light.position.set(2, 4, 2);

    const sphereGeo = new THREE.SphereGeometry(1.7, 64, 64);
    const sphereMat = new THREE.MeshStandardMaterial({
      color: 0xb9afa5,
      map: photoTex,
      envMapIntensity: 1.8,
      metalness: 0.0,
      roughness: 0.51,
      emissive: new THREE.Color(0x100500),
      emissiveIntensity: 0.15,
    });

    const sphereMesh = new THREE.Mesh(sphereGeo, sphereMat);
    sphereMesh.position.set(-3.5, 0.2, 1.5);
    sphereMesh.receiveShadow = true;
    sphereMesh.castShadow = true;
    scene.add(light, sphereMesh);

    const sandMat = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uWindStr: { value: P.windStrength },
        uWindDir: { value: windVec2() },
        uRipple: { value: P.windRipple },
        uLight: { value: new THREE.Color(P.sandLight) },
        uDark: { value: new THREE.Color(P.sandDark) },
        uCrest: { value: new THREE.Color(P.sandCrest) },
        uSunDir: { value: sunLight.position.clone().normalize() },
      },
      vertexShader: `
        varying vec3 vN;
        varying vec3 vW;
        varying vec2 vXZ;
        void main(){
          vN  = normalize(normalMatrix * normal);
          vec4 w = modelMatrix * vec4(position, 1.0);
          vW  = w.xyz;
          vXZ = w.xz;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        precision highp float;
        uniform float uTime;
        uniform float uWindStr;
        uniform vec2  uWindDir;
        uniform float uRipple;
        uniform vec3  uLight, uDark, uCrest;
        uniform vec3  uSunDir;
        varying vec3 vN, vW;
        varying vec2 vXZ;

        float hh(vec2 p){ return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453); }
        float nn(vec2 p){
          vec2 i=floor(p), f=fract(p), u=f*f*(3.-2.*f);
          return mix(mix(hh(i),hh(i+vec2(1,0)),u.x),
                     mix(hh(i+vec2(0,1)),hh(i+vec2(1,1)),u.x),u.y);
        }
        float fbm2(vec2 p){
          float v=0.,a=.5; mat2 m=mat2(1.6,1.2,-1.2,1.6);
          for(int i=0;i<5;i++){v+=a*nn(p);p=m*p;a*=.5;}
          return v;
        }

        void main(){
          vec3 N = normalize(vN);
          float  windTime   = uTime * uWindStr * 0.055;
          vec2   windOffset = uWindDir * windTime;
          vec2  perp2   = vec2(-uWindDir.y, uWindDir.x);
          float along   = dot(vXZ, uWindDir);
          float across  = dot(vXZ, perp2);
          vec2 uvBase   = vec2(along, across) * 0.06 + windOffset * 1.2;
          float dune = fbm2(uvBase * 0.7);
          float rippleFreq  = 18.0 * uRipple;
          float ripplePhase = along * 0.15 - windTime * 2.8;
          float warpU       = fbm2(uvBase * 1.5) * 1.4;
          float ripple      = sin(across * rippleFreq * 0.18 + warpU + ripplePhase);
          ripple = smoothstep(0.55, 1.0, ripple);
          vec2  grainUV   = vec2(along * 1.6 - windTime * 6.0, across * 4.5);
          float grain     = nn(grainUV) * nn(grainUV * 2.1 + windTime * 0.4);
          float diff = max(dot(N, uSunDir), 0.0);
          vec3 col = mix(uDark, uLight, diff * 0.8 + dune * 0.2);
          col += uCrest * ripple * pow(diff + 0.1, 2.5) * 0.7;
          col += uCrest * grain * uWindStr * 0.18;
          float rim = pow(max(1.0 - N.y, 0.0), 4.0);
          col += vec3(1.0, 0.5, 0.1) * rim * 0.35;
          float dfade = smoothstep(65., 4., length(vW.xz));
          col *= 0.55 + 0.45 * dfade;
          gl_FragColor = vec4(col, 1.0);
        }
      `,
    });

    const sandMesh = new THREE.Mesh(sandGeo, sandMat);
    sandMesh.receiveShadow = true;
    scene.add(sandMesh);

    // KTX2 + DRACO + GLTF terrain load
    const ktx2L = new KTX2Loader().setTranscoderPath("https://unpkg.com/three@0.165.0/examples/jsm/libs/basis/").detectSupport(renderer);
    const dracoL = new DRACOLoader().setDecoderPath("https://www.gstatic.com/draco/versioned/decoders/1.5.7/");
    const gltfL = new GLTFLoader().setDRACOLoader(dracoL).setKTX2Loader(ktx2L).setMeshoptDecoder(MeshoptDecoder);

    let glbMesh: any = null;
    gltfL.load(
      "/models/assets/terrain.glb",
      (g) => {
        g.scene.traverse((c: any) => {
          if (!c.isMesh) return;
          glbMesh = c;
          c.castShadow = c.receiveShadow = true;
          c.material.onBeforeCompile = (shader: any) => {
            shader.uniforms.uTime = sandMat.uniforms.uTime;
            shader.uniforms.uWindStr = sandMat.uniforms.uWindStr;
            shader.uniforms.uWindDir = sandMat.uniforms.uWindDir;
            shader.uniforms.uRipple = sandMat.uniforms.uRipple;
            shader.uniforms.uLight = sandMat.uniforms.uLight;
            shader.uniforms.uDark = sandMat.uniforms.uDark;
            shader.uniforms.uCrest = sandMat.uniforms.uCrest;

            shader.fragmentShader = `
              uniform float uTime;
              uniform float uWindStr;
              uniform vec2  uWindDir;
              uniform float uRipple;
              uniform vec3  uLight, uDark, uCrest;
              float hh(vec2 p){ return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453); }
              float nn(vec2 p){
                vec2 i=floor(p), f=fract(p), u=f*f*(3.-2.*f);
                return mix(mix(hh(i),hh(i+vec2(1,0)),u.x), mix(hh(i+vec2(0,1)),hh(i+vec2(1,1)),u.x),u.y);
              }
              float fbm2(vec2 p){
                float v=0.,a=.5; mat2 m=mat2(1.6,1.2,-1.2,1.6);
                for(int i=0;i<5;i++){v+=a*nn(p);p=m*p;a*=.5;}
                return v;
              }
            ` + shader.fragmentShader;

            shader.fragmentShader = shader.fragmentShader.replace(
              `#include <map_fragment>`,
              `
              #include <map_fragment>
              float mask = diffuseColor.r;
              vec2 vXZ = vMapUv * 100.0;
              float windTime   = uTime * uWindStr * 0.055 * mask;
              vec2  windOffset = uWindDir * windTime;
              vec2  perp2   = vec2(-uWindDir.y, uWindDir.x);
              float along   = dot(vXZ, uWindDir);
              float across  = dot(vXZ, perp2);
              vec2 uvBase   = vec2(along, across) * 0.06 + windOffset * 1.2;
              float rippleFreq  = 18.0 * uRipple;
              float ripplePhase = along * 0.15 - windTime * 2.8;
              float warpU       = fbm2(uvBase * 1.5) * 1.4;
              float ripple      = sin(across * rippleFreq * 0.18 + warpU + ripplePhase);
              ripple = smoothstep(0.55, 1.0, ripple) * mask;
              float grainUV_x   = along * 1.6 - windTime * 6.0;
              float grain       = nn(vec2(grainUV_x, across * 4.5)) * nn(vec2(grainUV_x * 2.1 + windTime * 0.4, across * 4.5));
              grain *= mask;
              vec3 sandBaseCol = mix(uDark, uLight, diffuseColor.rgb);
              sandBaseCol += uCrest * ripple * 0.4;
              sandBaseCol += uCrest * grain * uWindStr * 0.15;
              diffuseColor.rgb = mix(diffuseColor.rgb, sandBaseCol, mask);
              `
            );
          };
          c.material.needsUpdate = true;
        });
        g.scene.scale.set(5, 5, 5);
        g.scene.position.set(0, -8, 0);
        scene.add(g.scene);
        sandMesh.visible = false;
      }
    );

    // Floating rock creation
    const rGeo = new THREE.SphereGeometry(1.4, 64, 64);
    const rPos = rGeo.attributes.position;
    const _n3 = (x: number, y: number, z: number) => {
      let v = 0, a = 1;
      for (let i = 0; i < 5; i++) {
        const f = 1 << i;
        v += a * (Math.sin(x * f + 1.3) * Math.cos(y * f + 0.7) * Math.sin(z * f + 2.1));
        a *= 0.5;
      }
      return v;
    };
    for (let i = 0; i < rPos.count; i++) {
      const x = rPos.getX(i), y = rPos.getY(i) * 1.9, z = rPos.getZ(i);
      const l = Math.sqrt(x * x + y * y + z * z) || 1;
      const d = _n3((x / l) * 2.5, (y / l) * 2.5, (z / l) * 2.5) * 0.36;
      rPos.setXYZ(i, x * 0.78 + (x / l) * d, y + (y / l) * d, z * 0.78 + (z / l) * d);
    }
    rGeo.computeVertexNormals();

    const rockMat = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uLight: { value: sunLight.position.clone() },
      },
      vertexShader: `
        varying vec3 vN,vP,vW;
        void main(){
          vN=normalize(normalMatrix*normal); vP=position;
          vW=(modelMatrix*vec4(position,1.)).xyz;
          gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.);
        }
      `,
      fragmentShader: `
        precision highp float;
        uniform float uTime; uniform vec3 uLight;
        varying vec3 vN,vP,vW;
        float hh(vec3 p){return fract(sin(dot(p,vec3(127.1,311.7,74.7)))*43758.5);}
        float nn(vec3 p){
          vec3 i=floor(p),f=fract(p),u=f*f*(3.-2.*f);
          return mix(mix(mix(hh(i),hh(i+vec3(1,0,0)),u.x),mix(hh(i+vec3(0,1,0)),hh(i+vec3(1,1,0)),u.x),u.y),
                     mix(mix(hh(i+vec3(0,0,1)),hh(i+vec3(1,0,1)),u.x),mix(hh(i+vec3(0,1,1)),hh(i+vec3(1,1,1)),u.x),u.y),u.z);
        }
        float crack(vec3 p){
          float a=pow(abs(nn(p*3.)-0.5)*2.,0.3);
          return 1.-smoothstep(0.,0.28,a) + abs(nn(p*6.+1.7)-0.5)*0.4*(1.-a);
        }
        void main(){
          vec3 N=normalize(vN);
          vec3 L=normalize(uLight-vW);
          float d=max(dot(N,L),0.);
          float g=nn(vP*8.)*.35+.65;
          vec3 stone=mix(vec3(.25,.16,.09),vec3(.52,.36,.2),g);
          float c=crack(vP*1.8);
          float pulse=.72+.28*sin(uTime*2.4+vP.y*3.2+vP.x*2.1);
          vec3 lava=vec3(1.,.52,.02)*pulse*3.5;
          vec3 col=mix(stone,lava,c)*(d*.7+.3);
          float rim=pow(1.-max(dot(N,normalize(vec3(0,1,1))),0.),2.5);
          col+=vec3(1.,.48,.08)*rim*.9+lava*c*pulse*.8;
          gl_FragColor=vec4(col,1.);
        }
      `,
    });
    const rock = new THREE.Mesh(rGeo, rockMat);
    rock.position.set(0, 3.5, 0);
    scene.add(rock);

    // Dust particles
    const DCNT = 2800;
    const dpGeo = new THREE.BufferGeometry();
    const dpPos = new Float32Array(DCNT * 3);
    const dpSiz = new Float32Array(DCNT);
    for (let i = 0; i < DCNT; i++) {
      dpPos[i * 3] = (Math.random() - 0.5) * 80;
      dpPos[i * 3 + 1] = Math.random() * 7 - 1.5;
      dpPos[i * 3 + 2] = (Math.random() - 0.5) * 60;
      dpSiz[i] = Math.random() * 2.8 + 0.8;
    }
    dpGeo.setAttribute("position", new THREE.BufferAttribute(dpPos, 3));
    dpGeo.setAttribute("aS", new THREE.BufferAttribute(dpSiz, 1));

    const dustMat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uTime: { value: 0 },
        uWindStr: { value: P.windStrength },
        uWindDir: { value: windVec2() },
        uSize: { value: P.dustSize },
        uOpacity: { value: P.dustOpacity },
      },
      vertexShader: `
        attribute float aS;
        uniform float uTime,uWindStr,uSize;
        uniform vec2  uWindDir;
        varying float vA;
        void main(){
          vec3 pos=position;
          float spd = uWindStr * 1.8;
          pos.x += uWindDir.x*(uTime*spd) + sin(pos.z*.12+uTime*.6)*.5*uWindStr;
          pos.z += uWindDir.y*(uTime*spd*.3);
          pos.x  = mod(pos.x+40.,80.)-40.;
          pos.z  = mod(pos.z+30.,60.)-30.;
          float h=clamp((pos.y+1.5)/8.5,0.,1.);
          vA=(1.-h)*.6+.05;
          gl_Position=projectionMatrix*modelViewMatrix*vec4(pos,1.);
          gl_PointSize=aS*uSize*(14./length(gl_Position.xyz));
        }
      `,
      fragmentShader: `
        uniform float uOpacity;
        varying float vA;
        void main(){
          float d=length(gl_PointCoord-.5);
          if(d>.5)discard;
          gl_FragColor=vec4(1.,.68,.2,smoothstep(.5,.06,d)*vA*uOpacity);
        }
      `,
    });
    const dustPts = new THREE.Points(dpGeo, dustMat);
    scene.add(dustPts);

    // Custom manual bloom render targets
    const mkRT = (sw: number, sh: number) => new THREE.WebGLRenderTarget(sw, sh, { minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter });
    let rtMain = mkRT(W, H), rt1 = mkRT(W / 2, H / 2), rt2 = mkRT(W / 4, H / 4), rt3 = mkRT(W / 8, H / 8);

    const ppSc = new THREE.Scene();
    const ppCm = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const ppQ = new THREE.Mesh(new THREE.PlaneGeometry(2, 2));
    ppSc.add(ppQ);

    const extractM = new THREE.ShaderMaterial({
      uniforms: { tD: { value: null }, uTh: { value: 0.45 } },
      vertexShader: `varying vec2 v; void main(){v=uv;gl_Position=vec4(position.xy,0.,1.);}`,
      fragmentShader: `
        uniform sampler2D tD; uniform float uTh; varying vec2 v;
        void main(){
          vec4 c=texture2D(tD,v);
          float b=dot(c.rgb,vec3(.2126,.7152,.0722));
          gl_FragColor=c*smoothstep(uTh,uTh+.15,b);
        }
      `,
    });

    const blurM = new THREE.ShaderMaterial({
      uniforms: { tD: { value: null }, uDi: { value: new THREE.Vector2(1, 0) }, uRs: { value: new THREE.Vector2(W / 4, H / 4) } },
      vertexShader: `varying vec2 v; void main(){v=uv;gl_Position=vec4(position.xy,0.,1.);}`,
      fragmentShader: `
        uniform sampler2D tD; uniform vec2 uDi,uRs; varying vec2 v;
        void main(){
          vec2 o=uDi/uRs; vec4 c=vec4(0.); float ws[9],s=0.;
          ws[0]=.0625;ws[1]=.125;ws[2]=.25;ws[3]=.5;ws[4]=1.;ws[5]=.5;ws[6]=.25;ws[7]=.125;ws[8]=.0625;
          for(int i=0;i<9;i++){c+=texture2D(tD,v+o*(float(i)-4.))*ws[i];s+=ws[i];}
          gl_FragColor=c/s;
        }
      `,
    });

    const compM = new THREE.ShaderMaterial({
      uniforms: {
        tS: { value: null },
        tB: { value: null },
        uT: { value: 0 },
        uBl: { value: P.bloomStr },
        uVi: { value: P.vignette },
        uGr: { value: P.grain },
      },
      vertexShader: `varying vec2 v; void main(){v=uv;gl_Position=vec4(position.xy,0.,1.);}`,
      fragmentShader: `
        uniform sampler2D tS,tB; uniform float uT,uBl,uVi,uGr; varying vec2 v;
        float rr(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5);}
        void main(){
          vec3 c=texture2D(tS,v).rgb+texture2D(tB,v).rgb*uBl;
          vec2 uv=v*2.-1.;
          c*=mix(1.-uVi,1.,smoothstep(0.,1.,1.-dot(uv*.45,uv*.45)));
          c=pow(max(c,0.),vec3(.9,.96,1.1)); c.r*=1.08; c.b*=.72;
          c+=rr(v*900.+uT*60.)*uGr*2.-uGr;
          float sh=sin(v.y*85.+uT*3.)*.0016*max(0.,.55-v.y);
          c=mix(c,texture2D(tS,v+vec2(sh,0.)).rgb,.2);
          gl_FragColor=vec4(c,1.);
        }
      `,
    });

    // GUI load
    import("https://cdn.jsdelivr.net/npm/lil-gui@0.19/+esm" as any).then((guiModule: any) => {
      const GUI = guiModule.default || guiModule.GUI || guiModule;
      const gui = new GUI({ title: "Controls", width: 240 });

      const wf = gui.addFolder("🌬 Angin");
      wf.add(P, "windStrength", 0, 3, 0.01).name("kekuatan").onChange((v: number) => {
        sandMat.uniforms.uWindStr.value = v;
        dustMat.uniforms.uWindStr.value = v;
      });
      wf.add(P, "windAngle", 0, 360, 1).name("arah (°)").onChange(() => {
        const d = windVec2();
        sandMat.uniforms.uWindDir.value.copy(d);
        dustMat.uniforms.uWindDir.value.copy(d);
      });
      wf.add(P, "windRipple", 0.2, 4, 0.05).name("ripple lines").onChange((v: number) => (sandMat.uniforms.uRipple.value = v));

      const sf = gui.addFolder("🏜 Pasir");
      sf.addColor(P, "sandLight").name("terang").onChange((v: string) => sandMat.uniforms.uLight.value.set(v));
      sf.addColor(P, "sandDark").name("gelap").onChange((v: string) => sandMat.uniforms.uDark.value.set(v));
      sf.addColor(P, "sandCrest").name("crest").onChange((v: string) => sandMat.uniforms.uCrest.value.set(v));

      const df = gui.addFolder("✨ Debu");
      df.add(P, "dustSize", 0.5, 6, 0.1).name("ukuran").onChange((v: number) => (dustMat.uniforms.uSize.value = v));
      df.add(P, "dustOpacity", 0, 1, 0.01).name("opacity").onChange((v: number) => (dustMat.uniforms.uOpacity.value = v));

      const lf = gui.addFolder("💡 Cahaya");
      lf.addColor(P, "lightColor").name("warna").onChange((v: string) => sunLight.color.set(v));
      lf.add(P, "lightIntensity", 0, 8, 0.1).name("intensitas").onChange((v: number) => { sunLight.intensity = v; });

      const pf = gui.addFolder("🎬 Post-FX");
      pf.add(P, "bloomStr", 0, 5, 0.1).name("bloom").onChange((v: number) => (compM.uniforms.uBl.value = v));
      pf.add(P, "vignette", 0, 1, 0.01).name("vignette").onChange((v: number) => (compM.uniforms.uVi.value = v));
      pf.add(P, "grain", 0, 0.1, 0.001).name("grain").onChange((v: number) => (compM.uniforms.uGr.value = v));

      const cf = gui.addFolder("📷 Kamera");
      cf.add(P, "camY", 0, 14, 0.1).name("tinggi (Y)");
      cf.add(P, "camZ", 4, 50, 0.1).name("jarak (Z)");

      guiInstance = gui;
    }).catch(() => {});

    let reqId: number;
    function render() {
      reqId = requestAnimationFrame(render);
      const t = clock.getElapsedTime();

      sandMat.uniforms.uTime.value = t;
      rockMat.uniforms.uTime.value = t;
      dustMat.uniforms.uTime.value = t;
      compM.uniforms.uT.value = t;

      if (glbMesh?.material) {
        const wd = windVec2();
        const spd = 0.0028 * P.windStrength;
        ["map", "normalMap", "roughnessMap", "metalnessMap", "aoMap"].forEach((k) => {
          if (glbMesh.material[k]) {
            glbMesh.material[k].offset.x += wd.x * spd;
            glbMesh.material[k].offset.y += wd.y * spd * 0.3;
          }
        });
      }

      rock.position.y = 3.5 + Math.sin(t * 0.72) * 0.18 + Math.sin(t * 1.35) * 0.06;
      rock.rotation.y = t * 0.11;
      rock.rotation.z = Math.sin(t * 0.42) * 0.04;

      camera.position.set(Math.sin(t * 0.07) * 0.35, P.camY + Math.sin(t * 0.11) * 0.12, P.camZ);
      camera.lookAt(0, 1.8, 0);

      renderer.setRenderTarget(rtMain);
      renderer.autoClear = true;
      renderer.render(bgScene, bgCam);
      renderer.autoClear = false;
      renderer.render(scene, camera);
      renderer.autoClear = true;

      renderer.setRenderTarget(rt1);
      ppQ.material = extractM;
      extractM.uniforms.tD.value = rtMain.texture;
      renderer.render(ppSc, ppCm);

      renderer.setRenderTarget(rt2);
      ppQ.material = blurM;
      blurM.uniforms.tD.value = rt1.texture;
      blurM.uniforms.uDi.value.set(1, 0);
      blurM.uniforms.uRs.value.set(W / 2, H / 2);
      renderer.render(ppSc, ppCm);

      renderer.setRenderTarget(rt3);
      blurM.uniforms.tD.value = rt2.texture;
      blurM.uniforms.uDi.value.set(0, 1);
      blurM.uniforms.uRs.value.set(W / 8, H / 8);
      renderer.render(ppSc, ppCm);

      renderer.setRenderTarget(null);
      ppQ.material = compM;
      compM.uniforms.tS.value = rtMain.texture;
      compM.uniforms.tB.value = rt3.texture;
      renderer.render(ppSc, ppCm);
    }
    render();

    const handleResize = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      renderer.setSize(W, H);
      camera.aspect = W / H;
      camera.updateProjectionMatrix();
      skyMat.uniforms.uRes.value.set(W, H);
      rtMain.setSize(W, H);
      rt1.setSize(W / 2, H / 2);
      rt2.setSize(W / 4, H / 4);
      rt3.setSize(W / 8, H / 8);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(reqId);
      window.removeEventListener("resize", handleResize);
      if (guiInstance) guiInstance.destroy();
      renderer.dispose();
    };
  }, []);

  return (
    <div style={{ width: "100vw", height: "100vh", overflow: "hidden", background: "#090300", color: "#fff", position: "relative" }}>
      <style>{`
        *,*::before,*::after{margin:0;padding:0;box-sizing:border-box;}
        html,body{width:100%;height:100%;overflow:hidden;background:#090300;}
        #c{position:fixed;inset:0;width:100%;height:100%;display:block;}
        #ui{position:fixed;inset:0;pointer-events:none;z-index:10;}
        #logo{position:absolute;top:40px;left:40px;display:flex;align-items:center;gap:12px;color:white;}
        #logo svg{width:24px;height:24px;}
        #logo-text{font-family:'Inter',sans-serif;font-size:10px;font-weight:700;letter-spacing:0.15em;line-height:1.35;text-transform:uppercase;opacity:0.95;}
        #sound{position:absolute;top:40px;right:40px;display:flex;align-items:center;gap:8px;color:white;font-family:'Inter',sans-serif;font-size:10px;font-weight:700;letter-spacing:0.15em;opacity:0.9;}
        .sound-wave{display:inline-flex;align-items:flex-end;gap:2px;height:9px;width:20px;}
        .sound-bar{width:1.5px;background-color:white;border-radius:0.5px;animation:sound-pulse 0.8s ease-in-out infinite alternate;}
        .sound-bar:nth-child(1){height:40%;animation-delay:0.1s;}
        .sound-bar:nth-child(2){height:100%;animation-delay:0.4s;}
        .sound-bar:nth-child(3){height:60%;animation-delay:0.2s;}
        .sound-bar:nth-child(4){height:80%;animation-delay:0.5s;}
        .sound-bar:nth-child(5){height:50%;animation-delay:0.3s;}
        @keyframes sound-pulse{0%{transform:scaleY(0.3);}100%{transform:scaleY(1.0);}}
        #headline{position:absolute;bottom:50px;left:50px;max-width:650px;}
        #headline h1{color:white;font-family:'Syncopate',sans-serif;font-size:clamp(20px,2.8vw,36px);font-weight:700;letter-spacing:0.05em;text-transform:uppercase;line-height:1.25;text-shadow:0 4px 12px rgba(0,0,0,0.4);}
        #scroll-hint{position:absolute;bottom:50px;right:50px;display:flex;align-items:center;gap:12px;}
        .scroll-text{color:rgba(255,255,255,0.85);font-family:'Inter',sans-serif;font-size:8px;font-weight:700;letter-spacing:0.2em;text-align:right;text-transform:uppercase;line-height:1.5;}
        .scroll-line{width:1px;height:24px;background-color:rgba(255,255,255,0.5);}
      `}</style>

      <canvas ref={canvasRef} id="c" />

      <div id="ui">
        <div id="logo">
          <svg viewBox="0 0 36 36" fill="white" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 8 L18 8 L18 12 L10 12 L16 26 L12 26 Z" />
            <path d="M22 8 L32 8 L20 26 L17 26 Z" />
          </svg>
          <div id="logo-text">HASHGRAPH<br />VENTURES</div>
        </div>

        <div id="sound">
          <span>SOUND ON</span>
          <div className="sound-wave">
            <div className="sound-bar" />
            <div className="sound-bar" />
            <div className="sound-bar" />
            <div className="sound-bar" />
            <div className="sound-bar" />
          </div>
        </div>

        <div id="headline">
          <h1>THE NEXT WAVE<br />OF VENTURE CAPITAL</h1>
        </div>

        <div id="scroll-hint">
          <div className="scroll-text">
            SCROLL DOWN TO<br />DISCOVER MORE
          </div>
          <div className="scroll-line" />
        </div>
      </div>
    </div>
  );
}
